import {
	CurrentUserRoleLike,
	isOrganizationAdmin,
	isOrganizationAdminRole,
} from './hierarchySelection';
import { isHiddenSecurityRole } from '../constants';

export interface SecurityRoleLike extends CurrentUserRoleLike {
	'@id'?: string;
	workplace?: unknown;
	isSystemRole?: boolean;
}

const parseIdFromIri = (iri: string): number | null => {
	const parts = iri.split('/').filter(Boolean);
	if (!parts.length) return null;
	const num = Number(parts[parts.length - 1]);
	return Number.isNaN(num) ? null : num;
};

/**
 * Extracts the owning workplace id from a security role's `workplace` field,
 * which the API may return as an IRI string, an embedded object, or a number.
 * Returns null for organization-level roles (no workplace).
 */
export const getSecurityRoleWorkplaceId = (workplace: unknown): number | null => {
	if (workplace === null || workplace === undefined || workplace === '') return null;
	if (typeof workplace === 'number') return Number.isNaN(workplace) ? null : workplace;
	if (typeof workplace === 'string') return parseIdFromIri(workplace);
	if (typeof workplace === 'object') {
		const wp = workplace as { id?: unknown; '@id'?: unknown };
		if (typeof wp.id === 'number') return wp.id;
		if (typeof wp['@id'] === 'string') return parseIdFromIri(wp['@id']);
	}
	return null;
};

/**
 * A role is visible in a workplace when it is a system role, organization-level
 * (no workplace — visible in every workplace), or owned by that workplace.
 */
export const isSecurityRoleVisibleForWorkplace = (
	role: SecurityRoleLike,
	workplaceId: number | null
): boolean => {
	if (isHiddenSecurityRole(role?.name)) return false;
	if (role?.name && String(role.name).includes('BYOD')) return false;
	if (role?.isSystemRole) return true;
	const roleWorkplaceId = getSecurityRoleWorkplaceId(role?.workplace);
	if (roleWorkplaceId === null) return true;
	if (workplaceId === null) return false;
	return roleWorkplaceId === workplaceId;
};

export const filterSecurityRolesForWorkplace = <T extends SecurityRoleLike>(
	securityRoles: T[],
	workplaceId: number | null
): T[] => securityRoles.filter((role) => isSecurityRoleVisibleForWorkplace(role, workplaceId));

export const canAssignOrganizationAdminRole = (
	currentUserRole: CurrentUserRoleLike | null
): boolean => isOrganizationAdmin(currentUserRole);

/**
 * Who may change the Organization Administrator on the org record.
 * Same privilege set as assigning the Organization Admin security role
 * (Organization Admin role, or admin@byod.ai).
 */
export const canChangeOrganizationAdministrator = (
	currentUserRole: CurrentUserRoleLike | null
): boolean => canAssignOrganizationAdminRole(currentUserRole);

/**
 * Returns security roles the logged-in user is allowed to assign.
 * Only Organization Admins and admin@byod.ai may assign the Organization Admin role.
 */
export const getAssignableSecurityRoles = <T extends SecurityRoleLike>(
	securityRoles: T[],
	currentUserRole: CurrentUserRoleLike | null
): T[] => {
	if (canAssignOrganizationAdminRole(currentUserRole)) {
		return securityRoles;
	}

	return securityRoles.filter((role) => !isOrganizationAdminRole(role));
};

export const isSecurityRoleAssignmentPermitted = (
	securityRoleResource: string | undefined,
	securityRoles: SecurityRoleLike[],
	currentUserRole: CurrentUserRoleLike | null
): boolean => {
	if (!securityRoleResource) return false;

	const selectedRole = securityRoles.find((role) => role['@id'] === securityRoleResource);
	if (!selectedRole) return false;

	return getAssignableSecurityRoles(securityRoles, currentUserRole).some(
		(role) => role['@id'] === securityRoleResource
	);
};

/**
 * Users who cannot assign/view Organization Admin on create also cannot edit
 * users who already have that role.
 */
export const canEditUserWithSecurityRole = (
	targetUserRole: CurrentUserRoleLike | null,
	currentUserRole: CurrentUserRoleLike | null
): boolean => {
	if (!isOrganizationAdminRole(targetUserRole)) return true;
	return canAssignOrganizationAdminRole(currentUserRole);
};

export { isOrganizationAdminRole };
